import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import classNames from "classnames";
import * as React from "react";

import type { PlotPanelState } from "@/shared/state/visualization";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import {
  useTimeDisplayMode,
  useWorkspaceFiles,
  useWorkspaceTimeBounds,
} from "../../../WorkspaceCtx";
import type { SeriesAlignment } from "../alignment";
import type { HoverDatum } from "../PlotRenderer";
import { seriesDisplayLabels } from "../seriesDisplayLabels";

import styles from "./Tooltip.module.css";
import { type AlignmentCaveat, buildTooltipRow } from "./tooltipRow";

export interface HoverData {
  data: HoverDatum[];
  x: number;
  y: number;
}

interface TooltipBodyProps {
  /**
   * How each series plots in aligned mode; `undefined` when the panel plots
   * absolute time.
   */
  alignment: SeriesAlignment | undefined;
  hoverData: HoverData | null;
  state: PlotPanelState;
}

// The portal relocates only the DOM; the React tree keeps this component under
// the workspace providers, so the hooks below still find their contexts.
export function TooltipBody({ alignment, hoverData, state }: TooltipBodyProps) {
  const [timeDisplayMode] = useTimeDisplayMode();
  const files = useWorkspaceFiles();
  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });
  const seriesLabels = React.useMemo(
    () => seriesDisplayLabels(state.data, files),
    [state.data, files],
  );

  return (
    <Box
      bgcolor={(theme) => alpha(theme.palette.paper.main, 0.8)}
      className={classNames(styles.container, {
        [styles.hidden]: hoverData === null || hoverData.data.length === 0,
      })}
      color={(theme) => theme.palette.text.primary}
      component="ul"
      fontFamily={(theme) => theme.typography.caption.fontFamily}
      fontSize={(theme) => theme.typography.caption.fontSize}
      left={hoverData?.x}
      top={hoverData?.y}
    >
      {hoverData?.data.map((datum) => {
        const row = buildTooltipRow({
          alignment,
          datum,
          earliestTimeNs: timeBounds.earliest ?? null,
          labels: seriesLabels,
          mode: timeDisplayMode,
          series: state.data,
        });
        return (
          <li key={datum.datasetId}>
            <span
              className={styles.colorBox}
              style={{ backgroundColor: datum.color }}
            />
            <SeriesLabel label={row.label} />
            {row.time}, {row.value}
            <AlignmentCaveatIndicator caveat={row.caveat} />
          </li>
        );
      })}
    </Box>
  );
}

interface AlignmentCaveatIndicatorProps {
  caveat: AlignmentCaveat | undefined;
}

/**
 * Flags a row whose series does not share the panel's alignment; the ⚠ glyph
 * matches the legend's flag for the same fallback. The tooltip sets
 * `pointer-events: none`, so a hover title would never open: `role="img"` with
 * `aria-label` carries the explanation instead.
 *
 * Enumerated without a default so adding an `AlignmentCaveat` member forces a
 * decision here.
 */
function AlignmentCaveatIndicator({
  caveat,
}: AlignmentCaveatIndicatorProps): React.ReactElement | null {
  if (caveat === undefined) {
    return null;
  }
  switch (caveat) {
    case "fallback-anchor":
      return (
        <span
          aria-label="The panel's anchor could not be resolved for this series; it is aligned to the start of its own recording instead."
          className={styles.alignmentCaveat}
          role="img"
        >
          ⚠
        </span>
      );
    case "missing-alignment":
      return (
        <span
          aria-label="This series could not be aligned; it plots against the panel start, not the shared alignment."
          className={styles.alignmentCaveat}
          role="img"
        >
          (not aligned)
        </span>
      );
  }
}

interface SeriesLabelProps {
  /** The series' display label; `undefined` when the datum matched no series. */
  label: string | undefined;
}

function SeriesLabel({ label }: SeriesLabelProps) {
  if (label === undefined) {
    return null;
  }
  return <span className={styles.seriesLabel}>{label}:</span>;
}
